import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useInstallStatus } from "../api/install";
import { Loading } from "../components/ui";

/**
 * Installer-guard (SITE_SPEC §8.1) — court-circuite tout tant que le site n'est pas installé.
 *
 * - Lit `GET /api/install/status`. Tant que `installed === false`, toute route SAUF `/install`
 *   redirige vers `/install`.
 * - Symétriquement, une fois installé, visiter `/install` renvoie vers l'accueil (le wizard est
 *   verrouillé côté BFF, mais on évite l'écran mort).
 * - Si le statut est indisponible (BFF down) on laisse passer : le fallback d'erreur des pages
 *   gère la dégradation ; on ne bloque pas le site entier sur une lecture ratée.
 */
export function InstallGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data, isLoading, isError } = useInstallStatus();

  const onInstallRoute = location.pathname === "/install";

  if (isLoading) return <Loading className="py-32" />;

  // Statut indisponible → ne pas verrouiller le site.
  if (isError || !data) return <>{children}</>;

  if (!data.installed && !onInstallRoute) {
    return <Navigate to="/install" replace />;
  }
  if (data.installed && onInstallRoute) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
